import { EntryApi, FileApi } from '@api';
import type { MedicationList } from '@store/models/SystemFields/Medications/MedicationList';
import { toArrayFromIterable } from '@utils/toArrayFromIterable';
import compact from 'lodash/compact';
import uniq from 'lodash/uniq';

import { Optional } from 'chaika-common-types';
import { EntryTypeFieldType, DictionaryNames, Locale, SOAP_LAUNCH_DAY } from 'chaika-enums';
import { customFieldQueryOptions } from '@template/queries/customFieldQueries';
import { EntryPrintFormAdditionalSignatureParams, EntryPrintFormDataParams } from 'chaika-frontend-types/frontend-cms';
import { EntryPrintFormSignatureParams } from 'chaika-types';

import { EntryPrintFormApi } from '@api/EntryPrintFormApi';
import { getTranslatesForAllLocales, translate } from '@chaika/i18n/helper';
import { legalEntityService } from '@organization/service/legalEntity/legalEntityService';
import { legalEntityBranchService } from '@organization/service/legalEntityBranch/legalEntityBranchService';
import { EhrRelation, ehrService } from '@patient/services/ehr/ehrService';
import { entryPrintFormDataHelper } from '@patient/services/entry/entryPrintFormDataHelper';
import { EntryRelation, entryService } from '@patient/services/entry/entryService';
import { store } from '@store';
import { appService } from '@store/App/appService';
import { ENTRY_MEDICAL_CASE_BLOCK_ID } from '@store/types';
import { dictionaryService } from '@template/services/dictionary/dictionaryService';
import { EntryTypeBlock } from '@template/store/entryType/EntryTypeBlock';

import { EntryTypeSection } from '@template/store/entryType/EntryTypeSection';
import { ENTRY_FIELD_EXCEPTIONS } from '@template/store/entryType/types';
import { queryClient } from '@store/queryClient';
import { userQueryOptions } from '@user/queries/userQueries';
import { userProfileQueryOptions } from '@user/queries/userProfileQueries';
import { DateHelper } from '@utils/DateHelper';
import { Validation } from '@utils/Validation';
import { organizationFileService } from '@organization/service/organizaion/organizationFileService';

interface Params {
    isSoap?: boolean;
    isShortPrintForm?: boolean;
    isObjectiveForm?: boolean;
    ignoreHideFromPatient?: boolean;
    signature?: EntryPrintFormSignatureParams;
}

class EntryPrintFormService {
    private includedTitles: string[] = [
        ...getTranslatesForAllLocales('diagnosis'),
        ...getTranslatesForAllLocales('referralCut'),
        ...getTranslatesForAllLocales('conclusionCut'),
        ...getTranslatesForAllLocales('resultOfCut'),
    ].map(title => title.toLowerCase());

    private excludeTitles: string[] = [...getTranslatesForAllLocales('services')].map(title => title.toLowerCase());

    public async isSoapEntry(entryId: string): Promise<boolean> {
        const isSerialNumber = Validation.isNumberString(entryId);
        const [entry] = await EntryApi.getList(isSerialNumber ? { serialNumbers: entryId } : { ids: entryId });

        return !!entry && this.isSoapByCreationTime(entry.creationTime);
    }

    public isSoapByCreationTime(creationTime: string): boolean {
        return DateHelper.isAfter(creationTime, SOAP_LAUNCH_DAY);
    }

    public async getEntryHtml(
        entryIdOrSerialNumber: string,
        params: Params = {},
        withFiles: boolean = false,
    ): Promise<string> {
        await this.actualizeEntry(entryIdOrSerialNumber, params.isSoap);
        return this.makeEntryHtmlForNotSigned(store.entry.id, params, withFiles);
    }

    public async getSignedEntryPdf(entryId: string, params: Pick<Params, 'signature'> = {}): Promise<ArrayBuffer> {
        await this.loadData(entryId, true);
        const { isDisabledEntryTypeItemPresentations } = store.entry;
        const formParams = entryPrintFormDataHelper.makeEntryPrintData({
            isSoap: true,
            signature: params.signature,
            ignoreHideFromPatient: isDisabledEntryTypeItemPresentations,
        });

        const stampUrl = await this.getStampUrl();

        return EntryPrintFormApi.generatePdfAsBufferArray({
            entryId,
            entryData: {
                ...formParams,
                stampUrl,
            },
        });
    }

    public makeSignaturePrintAdditionalParams(): EntryPrintFormAdditionalSignatureParams {
        return {
            certificate: translate('certificate'),
            owner: translate('owner'),
            valid: translate('valid'),
            from: translate('from'),
            to: translate('to'),
            documentSignedByECP: translate('documentSignedByECP'),
        };
    }

    public isSectionHideInShortPrintForm(
        { title, blockIds, blocks }: EntryTypeSection,
        isShortPrintForm: boolean = false,
    ): boolean {
        return isShortPrintForm
            ? (this.includedTitles.every(name => !title.toLowerCase().includes(name)) &&
                  (blocks.every(block => this.isBlockHideInShortPrintForm(block, isShortPrintForm)) ||
                      blocks.every(block => !block.hasData)) &&
                  !new Set(blockIds).has(ENTRY_MEDICAL_CASE_BLOCK_ID)) ||
                  this.excludeTitles.some(name => title.toLowerCase() === name) ||
                  blocks.every(block => !(block.hasData || block.isEnableOnViewMode))
            : false;
    }

    public isBlockHideInShortPrintForm(
        { title, fieldNames }: Pick<EntryTypeBlock, 'title' | 'fieldNames'>,
        isShortPrintForm: boolean = false,
    ): boolean {
        return isShortPrintForm
            ? (this.includedTitles.every(name => !title.toLowerCase().includes(name)) &&
                  !fieldNames.some(name => ENTRY_FIELD_EXCEPTIONS.includes(name as EntryTypeFieldType))) ||
                  this.excludeTitles.some(hideTitle => hideTitle === title.toLowerCase())
            : false;
    }

    private async makeEntryHtmlForNotSigned(
        entryId: string,
        params: Params = {},
        withFiles: boolean = false,
    ): Promise<string> {
        const entryData = await this.makeEntryPrintData(entryId, params);
        return EntryPrintFormApi.createHtmlString(entryId, { ...entryData, files: withFiles ? this.getFiles() : [] });
    }

    private getFiles(): EntryPrintFormDataParams['files'] {
        const {
            app: { site },
        } = store;
        return store.entry.files.map(({ path, title }) => ({
            title: `${translate('downloadFile')}: ${title}`,
            url: `${site}/api/v1/mis/file-service/download?filename=${encodeURIComponent(path)}`,
        }));
    }

    private async getStampUrl(): Promise<Optional<string>> {
        try {
            const { organizationList } = store;
            let stampUrl: Optional<string>;
            let entryOrganization = organizationList.get(store.entry.organizationId);

            if (!entryOrganization || !entryOrganization.filesStrings?.stamp) {
                entryOrganization = await organizationFileService.reloadWithFiles(store.entry.organizationId);
            }

            if (entryOrganization.filesStrings?.stamp) {
                stampUrl = entryOrganization.filesStrings?.stamp;
            } else if (!!entryOrganization.stampPath) {
                stampUrl = await FileApi.makeSharedLink(entryOrganization.stampPath);
            }

            return stampUrl;
        } catch (e: any) {
            store.app.showPopup({
                title: translate('stampLoadingError'),
                description: e?.message ?? e,
            });

            return undefined;
        }
    }

    private async makeEntryPrintData(
        entryId: string,
        { ignoreHideFromPatient, ...params }: Params,
    ): Promise<EntryPrintFormDataParams> {
        await this.loadData(entryId, params.isSoap);
        const { isDisabledEntryTypeItemPresentations } = store.entry;
        return entryPrintFormDataHelper.makeEntryPrintData({
            ...params,
            ignoreHideFromPatient: isDisabledEntryTypeItemPresentations ? true : ignoreHideFromPatient,
        });
    }

    /** Состояния зубной формулы читаются из кэша синхронно, поэтому дерево нужно поднять заранее. */
    private async prefetchDentalConditions(): Promise<void> {
        await queryClient.fetchQuery(customFieldQueryOptions.byFieldType(EntryTypeFieldType.DentalTreatment));
    }

    // The print data is built synchronously (entryPrintFormDataHelper / EntryPrintFormParamsHelper
    // read users from the RQ cache), so every user the form mentions must be fetched up front.
    private async prefetchPrintFormUsers(): Promise<void> {
        const { entry, authUser } = store;

        const userIds = uniq(
            compact([
                entry.doctorId,
                entry.authorId,
                ...(entry.coAuthorIds ?? []),
                ...entry.medicalCases.values.flatMap(({ dto, authorId }) => [dto.managerId, authorId]),
                ...entry.drugList.values.map(({ authorId }) => authorId),
            ]),
        );

        if (userIds.length > 0) {
            await queryClient.fetchQuery(userQueryOptions.list({ ids: userIds }));
        }

        // The eng name for non-ru print locales lives on the profile payload only.
        const lang = entry.selectedLang || authUser.selectedLanguage;
        const engNameUserId = entry.doctorId || entry.authorId;
        if (lang !== Locale.Ru && lang !== Locale.Am && engNameUserId) {
            await queryClient.fetchQuery(userProfileQueryOptions.byUserId(engNameUserId));
        }
    }

    private async actualizeEntry(entryId: string, isSoap: boolean = false): Promise<void> {
        if (store.entry.id !== entryId) {
            await entryService.loadEntry(entryId, [
                isSoap ? EntryRelation.RedesignEntryExpandedData : EntryRelation.EntryType,
                EntryRelation.EntryTypeItemPresentation,
                EntryRelation.Translations,
            ]);
        }
    }

    private async loadData(entryId: string, isSoap = false): Promise<void> {
        await Promise.all([
            this.actualizeEntry(entryId, isSoap),
            appService.loadServicesConfig(),
            legalEntityService.loadAll(),
            dictionaryService.load(
                [DictionaryNames.Expertiza, DictionaryNames.InjectionSite, DictionaryNames.DeliveryMethod],
                { limit: 1000 },
            ),
            legalEntityBranchService.load(),
        ]);

        await this.prefetchPrintFormUsers();
        await this.prefetchDentalConditions();

        const entryDrugsDictionaryItemsIds = this.getEntryDrugsDictionaryItemsIds();

        const uniqDictionaryIds = uniq([
            ...store.entry.medicalCases.getAllDiagnoses().map(it => it.icdId),
            ...entryDrugsDictionaryItemsIds,
            ...this.getEntrySelectFieldItemIds(),
        ]);
        await Promise.all([
            dictionaryService.loadDictionaryItemsByIds(uniqDictionaryIds),
            dictionaryService.loadTranslatedDictionaryItemsByIds(uniqDictionaryIds),
        ]);
        if (store.ehr.id !== store.entry.ehrId) {
            await ehrService.load(store.entry.ehrId, [EhrRelation.Field]);
        }
    }

    private getEntrySelectFieldItemIds(): string[] {
        const { entry } = store;

        return compact(
            [entry.entryType, ...toArrayFromIterable(entry.soapEntryType)]
                .flatMap(entryType => entryType.sections)
                .flatMap(section => section.blocks)
                .flatMap(block => block.fields)
                .flatMap(field => {
                    if (field.type === EntryTypeFieldType.Select) {
                        return [entry.getField<string, string>(field.name).getValue()];
                    }

                    if (field.type === EntryTypeFieldType.MultiSelect) {
                        // Legacy imports may store '' here, which passes `?? []` checks, hence the explicit guard
                        const fieldValues = entry.getField<unknown, string>(field.name).getValue();

                        return Array.isArray(fieldValues) ? fieldValues.map((it: { value: string }) => it.value) : [];
                    }

                    return [];
                }),
        );
    }

    private getEntryDrugsDictionaryItemsIds(): string[] {
        const usedMedication = store.entry.getFieldValue<MedicationList>(EntryTypeFieldType.UsedMedications);
        const usedEhrMedication = store.entry.getEhrFieldValue<MedicationList>(EntryTypeFieldType.UsedMedications);

        const tradeNameIdsSet = new Set<string>();
        const activeSubstanceIdsSet = new Set<string>();

        usedMedication.forEach(medication => {
            if (medication.tradeNameIds) {
                medication.tradeNameIds.forEach(id => {
                    tradeNameIdsSet.add(id);
                });
            }
            if (medication.activeSubstanceId) {
                activeSubstanceIdsSet.add(medication.activeSubstanceId);
            }
        });
        usedEhrMedication.forEach(medication => {
            if (medication.tradeNameIds) {
                medication.tradeNameIds.forEach(id => {
                    tradeNameIdsSet.add(id);
                });
            }
            if (medication.activeSubstanceId) {
                activeSubstanceIdsSet.add(medication.activeSubstanceId);
            }
        });
        store.entry.drugList.values.forEach(drug => {
            if (drug.tradeNameIds) {
                drug.tradeNameIds.forEach(id => {
                    tradeNameIdsSet.add(id);
                });
            }
            if (drug.activeSubstance) {
                activeSubstanceIdsSet.add(drug.activeSubstance);
            }
        });

        return [...toArrayFromIterable(tradeNameIdsSet), ...toArrayFromIterable(activeSubstanceIdsSet)];
    }
}

export const entryPrintFormService = new EntryPrintFormService();
